import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";

import { WidgetScreen } from "../../common/WidgetScreen";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useWidgetTodoSummaryPreviewQuery } from "./useWidgetTodoSummaryPreviewQuery";
import { WATodoSummaryMutation } from "./__generated__/WATodoSummaryMutation.graphql";
import { WATodoSummaryQuery } from "./__generated__/WATodoSummaryQuery.graphql";

import { WATodoSummaryPreview } from "./WidgetTodoSummaryPreview";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddTodoSummary">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddTodoSummary">;
};

export const WATodoSummary = withWebModal(WATodoSummaryC, true);
function WATodoSummaryC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWATodoSummary {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

const WA_TODO_OVERVIEW_QUERY = graphql`
  query WATodoSummaryQuery {
    store {
      ...WidgetOverviewSettings_Store
    }
  }
`;

function InnerWATodoSummary(props: Props & { fetchKey: string }) {
  const [teamIds, setTeamIds] = useState(props.route.params.defaultTeams);
  const data = useLazyLoadQuery<WATodoSummaryQuery>(
    WA_TODO_OVERVIEW_QUERY,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const query = useWidgetTodoSummaryPreviewQuery({
    teamIds,
    storecastId: props.route.params.storecastId,
    todoCategoryId: props.route.params.categoryId,
  });

  const [createWidgetTodoSummary, loading] = useMutation<WATodoSummaryMutation>(
    graphql`
      mutation WATodoSummaryMutation(
        $storecastId: ID!
        $input: CreateWidgetTodoSummaryInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetTodoSummary(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      const position = props.route.params.position;
      createWidgetTodoSummary({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX: position.x + position.c * 2,
            positionY: position.y,
            size: WAWidgetSizeToOverviewSize(size),
            teamIds,
            todoCategoryId: props.route.params.categoryId,
          },
        },
        onError: (e) => {
          console.log("e", e);
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetTodoSummary,
      props.navigation,
      props.route.params.categoryId,
      props.route.params.position,
      props.route.params.storecastId,
      teamIds,
    ]
  );

  const renderPreview = useCallback(
    (size: WidgetSizeT) =>
      query ? <WATodoSummaryPreview query={query} /> : null,
    [query]
  );

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          onCreate={onCreate}
          loading={loading}
          unitSize={props.route.params.unitSize}
          maxSize={props.route.params.maxSize}
          widgetSizes={overviewWidgetSizes}
        >
          {renderPreview}
        </WASizePreview>
      }
      settings={
        <WidgetOverviewSettings
          store={data.store}
          teamIds={teamIds}
          setTeamIds={setTeamIds}
        />
      }
    />
  );
}
